import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { nextSteppedValue } from "@/lib/numberFieldStep";
import { cn } from "@/lib/utils";

function layoutClass(className?: string) {
  if (!className) return "";
  return className
    .split(/\s+/)
    .filter((token) => {
      if (!token) return false;
      // El padding era del input viejo. Con prefijo (md:px-2) se colaba al
      // recuadro y las flechas quedaban antes del borde derecho.
      const bare = token.replace(/^(?:[a-z-]+:)*/, "");
      if (/^(?:px-|py-|pt-|pr-|pb-|pl-|p-)/.test(bare)) return false;
      if (/(?:^|:)(?:focus|focus-visible):/.test(token)) return false;
      return true;
    })
    .join(" ");
}

function NumberField({
  className,
  type: _type,
  value,
  disabled,
  min,
  max,
  step,
  onChange,
  ...props
}: React.ComponentProps<"input">) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const blockWheel = (event: WheelEvent) => event.preventDefault();
    el.addEventListener("wheel", blockWheel, { passive: false });
    return () => el.removeEventListener("wheel", blockWheel);
  }, []);
  const layout = layoutClass(className);
  const compact = /\b(py-1|h-7|h-8|text-center|text-xs)\b/.test(className ?? "");
  const align = /\btext-center\b/.test(className ?? "")
    ? "text-center"
    : /\btext-right\b/.test(className ?? "")
      ? "text-right"
      : "text-left";

  const readRaw = () => {
    if (value != null) return String(value);
    return inputRef.current?.value ?? "";
  };

  const stepBy = (dir: 1 | -1) => {
    const next = nextSteppedValue(readRaw(), dir, step, min, max);
    if (next == null) return;
    onChange?.({
      target: { value: next },
      currentTarget: { value: next },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const upDisabled = disabled || nextSteppedValue(readRaw(), 1, step, min, max) == null;
  const downDisabled = disabled || nextSteppedValue(readRaw(), -1, step, min, max) == null;

  return (
    <div
      data-slot="number-field"
      className={cn(
        "border-input flex h-9 w-full min-w-0 items-stretch overflow-hidden rounded-md border bg-transparent shadow-xs dark:bg-input/30",
        "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
        "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
        compact && "h-8",
        layout,
      )}
    >
      <input
        ref={inputRef}
        type="number"
        value={value}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        className={cn(
          "h-full min-w-0 flex-1 border-0 bg-transparent py-0 text-sm outline-none [appearance:textfield] placeholder:text-muted-foreground disabled:cursor-not-allowed [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          align === "text-center" && "px-1 text-center",
          align === "text-right" && "pr-1 pl-2 text-right",
          align === "text-left" && "pr-1 pl-3 text-left",
          /\btext-xs\b/.test(className ?? "") && "text-xs",
        )}
        {...props}
      />
      <span className="border-input flex w-[18px] shrink-0 flex-col border-l">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Subir"
          disabled={upDisabled}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => stepBy(1)}
          className={cn(
            "text-muted-foreground hover:bg-muted dark:text-foreground flex flex-1 items-center justify-center disabled:pointer-events-none",
            !disabled && "disabled:opacity-40",
          )}
        >
          <ChevronUp className="h-3 w-3" strokeWidth={2} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label="Bajar"
          disabled={downDisabled}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => stepBy(-1)}
          className={cn(
            "text-muted-foreground hover:bg-muted dark:text-foreground flex flex-1 items-center justify-center disabled:pointer-events-none",
            !disabled && "disabled:opacity-40",
          )}
        >
          <ChevronDown className="h-3 w-3" strokeWidth={2} />
        </button>
      </span>
    </div>
  );
}

export { NumberField };
